<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          Create Claim
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex h-full w-full overflow-y-auto">
        <div class="flex flex-col w-full my-2 px-3">
          <form
            action=""
            class="w-full flex flex-col space-y-4"
            @submit.prevent="createClaim"
          >
            <p>
              Creating claim for <b>{{ props.projectSupplier.supplier.name }}</b>

              for package <b>{{ props.package.name }}</b>
            </p>

            <p v-if="claimForm.form.start && claimForm.form.end">
              Overriding next claim period to be <b>{{ DateTime.fromFormat(claimForm.form.start, 'yyyy-MM-dd').toLocaleString() }}</b>

              to <b>{{ DateTime.fromFormat(claimForm.form.end, 'yyyy-MM-dd').toLocaleString() }}</b>
            </p>

            <p v-else-if="latestClaim">
              Next claim period will be <b>{{ latestClaim.startDate?.plus({ months: 1 }).startOf('month').toLocaleString() }}</b>

              to <b>{{ latestClaim.endDate?.plus({ months: 1 }).endOf('month').toLocaleString() }}</b>
            </p>

            <p v-else>
              First claim period will be <b>{{ DateTime.fromISO(project.claim_start).startOf('month').toLocaleString() }}</b>

              to <b>{{ DateTime.fromISO(project.claim_start).startOf('month').endOf('month').toLocaleString() }}</b>
            </p>

            <div
              v-if="isClaimPeriodMismatch"
              class="bg-yellow-100 text-yellow-800 p-4 rounded-md text-sm"
            >
              <p>
                You are viewing claims for the month of <b>{{ $props.selectedMonth.toLocaleString() }}.</b>

                The new claim will cover <b>{{ nextClaimPeriod?.start.toLocaleString() }}</b>

                to <b>{{ nextClaimPeriod?.end.toLocaleString() }}</b>. If this is not intended, you can override the claim period below.
              </p>

              <button
                type="button"
                class="btn btn--secondary-yellow mt-2 w-full"
                @click="overridePeriod"
              >
                Override period
              </button>
            </div>

            <label>
              Start Override
              <AppInput
                v-model="claimForm.form.start"
                name="start"
                type="date"
                class="form-input"
                :error="claimForm.getErrors('start')"
              />
            </label>

            <label>
              End Override
              <AppInput
                v-model="claimForm.form.end"
                name="end"
                type="date"
                class="form-input"
                :error="claimForm.getErrors('end')"
              />
            </label>
          </form>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <div>
          <button
            type="button"
            class="btn btn--secondary-blue"
            @click.prevent="$emit('close')"
          >
            Cancel
          </button>

          <button
            type="button"
            class="btn btn--primary-blue ml-2"
            @click="createClaim"
          >
            Create
          </button>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { useFormData } from '@/composables/useFormData';
import { useStoreApiAction } from '@/composables/useStoreApiAction';
import Claim from '@/models/Claim';
import Package from '@/models/Package';
import ProjectSupplier from '@/models/ProjectSupplier';
import { useClaimsStore } from '@/store/claims';
import { useProjectsStore } from '@/store/projects';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';
import { computed } from 'vue';
import { useRouter } from 'vue-router';

const props = withDefaults(
  defineProps<{
    projectId: Id;
    latestClaim?: Claim;
    projectSupplier: ProjectSupplier;
    package: Package;
    selectedMonth: DateTime;
  }>(),
  {
    latestClaim: undefined,
  },
);

const emits = defineEmits<{
  (e: 'close');
}>();

const claimsStore = useClaimsStore();
const router = useRouter();

const claimForm = useFormData({
  start: '',
  end: '',
  package_id: props.package.id,
});

const storeSubcontractorClaimAction = useStoreApiAction(claimsStore.storeSubcontractorClaim);

const createClaim = () => {
  claimForm.resetErrors();

  storeSubcontractorClaimAction.request(props.projectId, props.projectSupplier.id, claimForm.form).then((claim) => {
    Swal.fire({
      icon: 'success',
      title: 'Claim Created',
    }).then(() => {
      emits('close');
    });
  }).catch((error) => {
    if(error.status === 422) {
      if(error.data?.errors?.no_package_items) {
        Swal.fire({
          icon: 'error',
          text: error.data.errors.no_package_items,
          confirmButtonText: 'Add Package Items',
          showCancelButton: true,
        }).then((result) => {
          if(result.isConfirmed) {
            router.push({ name: 'PackagesDashboard', params: { packageId: props.package.id } });
          }
        });
      } else if(error.data?.errors?.package || error.data?.errors?.procurement_method) {
        Swal.fire({
          icon: 'error',
          text: error.data.message,
        });
      } else {
        claimForm.setErrors(error.data);
      }
    }
  });
};

const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const nextClaimPeriod = computed(() => {
  if(claimForm.form.start && claimForm.form.end) {
    return {
      start: DateTime.fromFormat(claimForm.form.start, 'yyyy-MM-dd'),
      end: DateTime.fromFormat(claimForm.form.end, 'yyyy-MM-dd'),
    };
  }

  if(props.latestClaim) {
    return {
      start: props.latestClaim.startDate?.plus({ months: 1 }).startOf('month'),
      end: props.latestClaim.endDate?.plus({ months: 1 }).endOf('month'),
    };
  }

  if(!project.value) {
    return undefined;
  }

  return {
    start: DateTime.fromISO(project.value.claim_start).startOf('month'),
    end: DateTime.fromISO(project.value.claim_start).startOf('month').endOf('month'),
  };
});

const isClaimPeriodMismatch = computed(() => {
  if(!nextClaimPeriod.value?.start) {
    return false;
  }

  return nextClaimPeriod.value.start.toFormat('yyyy-MM') !== props.selectedMonth.toFormat('yyyy-MM');
});

const overridePeriod = () => {
  claimForm.form.start = props.selectedMonth.startOf('month').toFormat('yyyy-MM-dd');
  claimForm.form.end = props.selectedMonth.endOf('month').toFormat('yyyy-MM-dd');
};
</script>

<style scoped></style>
